export interface Article {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  categoryId: string;
  author: string;
  date: string;
  readTime: string;
  image: string;
  tags: string[];
}

export const categories = [
  {
    id: 'getting-started',
    name: '快速入门',
    icon: 'ri-rocket-line',
    description: '从零开始了解 mrds，快速上手使用',
  },
  {
    id: 'features',
    name: '功能详解',
    icon: 'ri-stack-line',
    description: '深入了解 mrds 各项核心功能',
  },
  {
    id: 'scenarios',
    name: '应用场景',
    icon: 'ri-lightbulb-line',
    description: '探索 mrds 在不同领域的实际应用',
  },
  {
    id: 'best-practices',
    name: '最佳实践',
    icon: 'ri-award-line',
    description: '行业专家分享的 mrds 使用技巧',
  },
  {
    id: 'updates',
    name: '更新动态',
    icon: 'ri-notification-3-line',
    description: 'mrds 最新版本更新与产品动态',
  },
  {
    id: 'faq',
    name: '常见问题',
    icon: 'ri-question-answer-line',
    description: '关于 mrds 的常见问题解答',
  },
];

export const articles: Article[] = [
  {
    id: 'article-1',
    slug: 'mrds-quick-start-guide',
    title: 'mrds 快速上手指南：十分钟掌握核心操作',
    summary: '本文将带您快速了解 mrds 的基本操作流程，从注册账号到完成首个项目，只需十分钟即可掌握 mrds 的核心使用方法。',
    content: `<p>欢迎来到 mrds 官方平台！无论您是初次接触 mrds 的新用户，还是希望深入了解 mrds 功能的老用户，本指南都将帮助您快速掌握 mrds 的核心操作。</p>
<h2>第一步：注册与登录</h2>
<p>访问 mrds 官方网站，点击右上角的"注册"按钮，使用邮箱或手机号完成注册。注册成功后，系统将自动引导您完成初始设置。</p>
<h2>第二步：创建您的第一个项目</h2>
<p>登录 mrds 平台后，点击控制台中的"新建项目"按钮。mrds 提供了多种项目模板供您选择，您可以根据实际需求选择合适的模板快速启动项目。</p>
<h2>第三步：配置核心参数</h2>
<p>在项目创建完成后，进入项目设置页面。mrds 的核心配置包括数据源连接、权限管理和自动化规则设置。建议您按照系统引导逐步完成配置。</p>
<h2>第四步：开始使用 mrds 核心功能</h2>
<p>mrds 提供了丰富的数据处理、智能分析和自动报告生成功能。您可以在功能面板中找到所有可用工具，并通过拖拽方式进行个性化布局。</p>
<h2>总结</h2>
<p>通过以上四个步骤，您已经成功掌握了 mrds 的基本使用方法。mrds 还提供了更多高级功能等待您去探索。如有任何疑问，欢迎查阅我们的详细文档或联系客服团队。</p>`,
    categoryId: 'getting-started',
    author: 'mrds 官方团队',
    date: '2026-08-05',
    readTime: '8 分钟',
    image: 'https://readdy.ai/api/search-image?query=Modern%20minimalist%20workspace%20with%20laptop%20displaying%20professional%20dashboard%20interface%2C%20warm%20lighting%2C%20clean%20desk%20setup%2C%20organized%20workspace%2C%20professional%20atmosphere%2C%20soft%20natural%20light%20from%20window%2C%20coffee%20cup%20on%20desk%2C%20ergonomic%20setup&width=800&height=450&seq=article-quick-start&orientation=landscape',
    tags: ['mrds 入门', 'mrds 教程', '快速上手'],
  },
  {
    id: 'article-2',
    slug: 'mrds-core-features-overview',
    title: 'mrds 核心功能全面解析：五大模块深度评测',
    summary: '深入剖析 mrds 的五大核心功能模块，包括智能数据处理、自动化工作流、实时协作、数据可视化和安全管控，帮助您全面了解 mrds 的强大能力。',
    content: `<p>mrds 作为业界领先的专业平台，其核心功能模块设计精良，能够满足不同规模企业的多样化需求。本文将对 mrds 的五大核心功能进行全面解析。</p>
<h2>智能数据处理</h2>
<p>mrds 的数据处理引擎采用了先进的算法架构，支持多种数据格式的导入、清洗和转换。无论是结构化数据还是非结构化数据，mrds 都能高效处理，确保数据质量和一致性。</p>
<h2>自动化工作流</h2>
<p>mrds 的可视化工作流设计器让您无需编写代码即可创建复杂的自动化流程。通过拖拽组件并配置触发条件，您可以将重复性任务实现全自动化处理，大幅提升工作效率。</p>
<h2>实时协作</h2>
<p>mrds 支持多用户实时协作，团队成员可以同时编辑文档、查看数据变化并即时沟通。所有操作都有完整的版本历史记录，确保协作过程可追溯。</p>
<h2>数据可视化</h2>
<p>mrds 内置了丰富的数据可视化组件，支持柱状图、折线图、饼图、热力图等数十种图表类型。您可以通过简单的配置，将复杂数据转化为直观的可视化报告。</p>
<h2>安全管控</h2>
<p>mrds 采用多层安全防护机制，包括数据加密传输、细粒度权限控制、操作审计日志等功能，全方位保障您的数据安全。</p>`,
    categoryId: 'features',
    author: 'mrds 产品团队',
    date: '2026-07-28',
    readTime: '12 分钟',
    image: 'https://readdy.ai/api/search-image?query=Modern%20abstract%20technology%20concept%2C%20interconnected%20nodes%20and%20data%20flow%20visualization%2C%20geometric%20patterns%2C%20warm%20amber%20and%20emerald%20green%20accents%2C%20dark%20background%2C%20clean%20futuristic%20aesthetic%2C%20digital%20transformation%20theme&width=800&height=450&seq=article-features&orientation=landscape',
    tags: ['mrds 功能', '核心模块', '功能评测'],
  },
  {
    id: 'article-3',
    slug: 'mrds-enterprise-application',
    title: 'mrds 在企业数字化转型中的应用实践',
    summary: '通过真实案例，展示 mrds 如何帮助传统企业实现数字化转型，从流程优化到数据驱动决策，全面赋能企业业务增长。',
    content: `<p>数字化转型已成为企业发展的必经之路。mrds 凭借其强大的功能和灵活的部署方式，正在帮助越来越多的企业实现数字化升级。</p>
<h2>背景与挑战</h2>
<p>传统企业在数字化转型过程中面临数据孤岛、流程分散、技术门槛高等多重挑战。mrds 针对这些痛点提供了一站式解决方案。</p>
<h2>mrds 解决方案</h2>
<p>通过部署 mrds 平台，企业可以实现数据集中管理、业务流程自动化和决策智能化。mrds 的低代码特性使得非技术人员也能快速上手使用。</p>
<h2>实践案例</h2>
<p>某制造企业借助 mrds 平台，将生产数据实时采集与分析，实现了设备故障预测准确率提升 40%，生产效率提高 25%。</p>
<h2>总结与展望</h2>
<p>mrds 在企业数字化转型中扮演着越来越重要的角色。随着 mrds 功能的持续迭代更新，未来将为更多企业创造更大价值。</p>`,
    categoryId: 'scenarios',
    author: 'mrds 解决方案团队',
    date: '2026-07-20',
    readTime: '10 分钟',
    image: 'https://readdy.ai/api/search-image?query=Modern%20enterprise%20office%20environment%2C%20digital%20transformation%20concept%2C%20large%20display%20screens%20showing%20data%20dashboards%2C%20professional%20team%20collaborating%2C%20clean%20corporate%20aesthetic%2C%20warm%20natural%20lighting%2C%20plants%20in%20office&width=800&height=450&seq=article-enterprise&orientation=landscape',
    tags: ['mrds 应用', '数字化转型', '企业案例'],
  },
  {
    id: 'article-4',
    slug: 'mrds-best-practices-optimization',
    title: 'mrds 最佳实践：提升平台使用效率的十个技巧',
    summary: '总结了 mrds 资深用户和专家的使用经验，分享十个实用技巧，帮助您更高效地使用 mrds 平台，充分发挥其全部潜力。',
    content: `<p>在长期使用 mrds 的过程中，我们总结了许多提升使用效率的实用技巧。本文精选了十个最有价值的 mrds 使用技巧与您分享。</p>
<h2>技巧一：善用快捷键</h2>
<p>mrds 提供了丰富的键盘快捷键支持，熟练掌握这些快捷键可以大幅提升您的操作效率。</p>
<h2>技巧二：自定义工作区</h2>
<p>根据您的工作习惯自定义 mrds 工作区布局，将最常用的功能模块放置在触手可及的位置。</p>
<h2>技巧三：设置自动化规则</h2>
<p>充分利用 mrds 的自动化功能，将日常重复性任务交给系统自动处理，释放更多时间用于创造性工作。</p>
<h2>技巧四：定期数据备份</h2>
<p>虽然 mrds 提供了自动备份功能，但我们仍建议您定期进行手动备份，确保重要数据万无一失。</p>
<h2>总结</h2>
<p>以上十个技巧涵盖了 mrds 日常使用的方方面面。掌握这些技巧后，您将能够更加游刃有余地使用 mrds 平台。</p>`,
    categoryId: 'best-practices',
    author: 'mrds 社区专家',
    date: '2026-07-15',
    readTime: '15 分钟',
    image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20desk%20setup%20with%20multiple%20monitors%2C%20productivity%20concept%2C%20organized%20workspace%20with%20notebook%20and%20pen%2C%20warm%20amber%20accent%20lighting%2C%20minimalist%20aesthetic%2C%20professional%20environment%2C%20soft%20natural%20light&width=800&height=450&seq=article-best-practices&orientation=landscape',
    tags: ['mrds 技巧', '使用效率', '最佳实践'],
  },
  {
    id: 'article-5',
    slug: 'mrds-v3-update-features',
    title: 'mrds V3.0 重磅更新：全新界面与功能升级',
    summary: 'mrds V3.0 正式发布，带来全新的用户界面设计、性能大幅优化和多项新功能，本文将详细介绍本次更新的全部内容。',
    content: `<p>我们很高兴地宣布，mrds V3.0 版本正式发布！本次更新是 mrds 平台迄今为止最大规模的版本升级，涵盖界面、性能和功能三大方面。</p>
<h2>全新用户界面</h2>
<p>mrds V3.0 采用了全新的设计语言，界面更加简洁直观。重新设计的导航系统让功能切换更加流畅，新的深色模式也正式上线。</p>
<h2>性能大幅提升</h2>
<p>经过底层架构的全面重构，mrds V3.0 的页面加载速度提升了 60%，数据处理效率提升了 45%。</p>
<h2>新增功能</h2>
<p>本次更新新增了智能推荐引擎、多维度数据分析看板和团队协作空间等多项功能。</p>
<h2>升级指南</h2>
<p>现有用户可以通过平台内的自动更新功能完成升级，所有历史数据将无缝迁移到新版本。</p>`,
    categoryId: 'updates',
    author: 'mrds 开发团队',
    date: '2026-07-01',
    readTime: '6 分钟',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20abstract%20technology%20update%20concept%2C%20glowing%20nodes%20and%20connections%2C%20digital%20innovation%20theme%2C%20warm%20golden%20and%20emerald%20color%20accents%2C%20minimalist%20composition%2C%20clean%20geometric%20shapes&width=800&height=450&seq=article-update&orientation=landscape',
    tags: ['mrds 更新', 'V3.0', '新功能'],
  },
  {
    id: 'article-6',
    slug: 'mrds-faq-common-issues',
    title: 'mrds 常见问题解答：注册、付费与技术支持的全面指南',
    summary: '整理了 mrds 用户最常咨询的问题，涵盖账号注册、付费方案、技术支持和平台使用等方面，为您提供一站式解答。',
    content: `<p>本文整理了 mrds 用户在使用过程中最常遇到的问题及详细解答，帮助您快速找到所需的答案。</p>
<h2>账号相关</h2>
<p><strong>问：如何注册 mrds 账号？</strong></p>
<p>答：您可以访问 mrds 官网，点击注册按钮，使用邮箱或手机号完成注册。整个过程不超过两分钟。</p>
<p><strong>问：忘记密码怎么办？</strong></p>
<p>答：在登录页面点击"忘记密码"，输入注册邮箱即可收到重置链接。</p>
<h2>付费相关</h2>
<p><strong>问：mrds 有免费版本吗？</strong></p>
<p>答：是的，mrds 提供功能完善的免费版本，适合个人用户和小团队使用。需要更多高级功能可以升级到专业版或企业版。</p>
<h2>技术支持</h2>
<p><strong>问：如何获取技术支持？</strong></p>
<p>答：您可以通过平台内的在线客服、官方社区或邮件方式获取技术支持，专业团队将在 24 小时内响应。</p>`,
    categoryId: 'faq',
    author: 'mrds 客户支持',
    date: '2026-06-25',
    readTime: '5 分钟',
    image: 'https://readdy.ai/api/search-image?query=Friendly%20customer%20support%20concept%2C%20modern%20help%20desk%20environment%2C%20warm%20lighting%2C%20professional%20yet%20approachable%20atmosphere%2C%20clean%20minimalist%20office%20with%20plants&width=800&height=450&seq=article-faq&orientation=landscape',
    tags: ['mrds FAQ', '常见问题', '技术支持'],
  },
  {
    id: 'article-7',
    slug: 'mrds-data-security-guide',
    title: 'mrds 数据安全保障体系深度解读',
    summary: '全面解读 mrds 平台的数据安全保障体系，包括加密传输、权限管理、审计追踪等多层防护机制，让您安心使用 mrds。',
    content: `<p>数据安全是 mrds 平台的核心基石。我们投入了大量资源构建完善的安全防护体系，确保您的数据始终安全可靠。</p>
<h2>传输层安全</h2>
<p>mrds 全站采用 TLS 1.3 加密协议，确保数据在传输过程中的安全性。所有 API 接口均通过 HTTPS 协议进行通信。</p>
<h2>存储层安全</h2>
<p>mrds 使用 AES-256 加密标准对存储数据进行加密，即使物理存储介质被非法获取，数据内容也无法被解读。</p>
<h2>权限管控</h2>
<p>mrds 提供基于角色的访问控制（RBAC）机制，您可以为不同团队成员设置精细的数据访问权限。</p>
<h2>合规认证</h2>
<p>mrds 已通过 ISO 27001 信息安全管理体系认证和 SOC 2 Type II 审计，满足企业级安全合规要求。</p>`,
    categoryId: 'features',
    author: 'mrds 安全团队',
    date: '2026-06-18',
    readTime: '9 分钟',
    image: 'https://readdy.ai/api/search-image?query=Abstract%20digital%20security%20concept%2C%20shield%20and%20lock%20symbols%20in%20modern%20geometric%20design%2C%20warm%20golden%20glow%20effects%2C%20dark%20background%2C%20professional%20cybersecurity%20aesthetic&width=800&height=450&seq=article-security&orientation=landscape',
    tags: ['mrds 安全', '数据保护', '加密'],
  },
  {
    id: 'article-8',
    slug: 'mrds-sme-growth-toolkit',
    title: 'mrds 中小企业增长工具包：低成本的数字化方案',
    summary: '专为中小企业量身定制的 mrds 使用方案，以最低成本实现最大化的数字化效益，助力中小企业快速成长。',
    content: `<p>中小企业常常面临预算有限、技术人才不足等现实挑战。mrds 特别推出中小企业增长工具包，帮助您以最低成本实现数字化转型。</p>
<h2>为什么选择 mrds？</h2>
<p>mrds 的灵活定价模式和低代码特性，使中小企业无需投入大量资金和专业技术人才，即可享受专业级的数字化服务。</p>
<h2>核心功能组合</h2>
<p>mrds 为中小企业精心搭配了数据分析、客户管理和自动化营销三大核心功能模块，覆盖业务运营的关键环节。</p>
<h2>实施路径</h2>
<p>我们建议中小企业按照"数据整合→流程优化→智能决策"的三步路径，循序渐进地推进数字化建设。</p>
<h2>成功案例</h2>
<p>多家中小企业在使用 mrds 后，运营成本平均降低了 30%，客户满意度提升了 45%。</p>`,
    categoryId: 'scenarios',
    author: 'mrds 商业咨询团队',
    date: '2026-06-10',
    readTime: '7 分钟',
    image: 'https://readdy.ai/api/search-image?query=Small%20business%20growth%20concept%2C%20modern%20cozy%20office%20with%20small%20team%20working%2C%20growth%20charts%20on%20screen%2C%20warm%20inviting%20atmosphere%2C%20plants%20and%20natural%20light%2C%20professional%20yet%20casual%20setting&width=800&height=450&seq=article-sme&orientation=landscape',
    tags: ['mrds 中小企业', '数字化方案', '增长工具'],
  },
];